import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, FileText } from "lucide-react";

export default function Processes() {
  const [processes, setProcesses] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [gen, setGen] = useState({ title: "", context: "" });
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", owner: "", content: "" });

  const load = () => api.get("/processes").then((r) => setProcesses(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) return;
    await api.post("/processes", form);
    setOpen(false); setForm({ title: "", owner: "", content: "" });
    toast.success("SOP added.");
    load();
  };

  const generate = async () => {
    if (!gen.title.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/processes/generate", gen);
      toast.success("SOP generated by AI.");
      setGenOpen(false); setGen({ title: "", context: "" });
      load();
      setActive(data);
    } catch (e) {
      toast.error("Generation failed. Check API key balance.");
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!active) return;
    await api.put(`/processes/${active.id}`, { title: active.title, owner: active.owner || "", content: active.content || "" });
    toast.success("Saved.");
    load();
  };

  const remove = async (id) => { if (confirm("Delete this SOP?")) { await api.delete(`/processes/${id}`); if (active?.id===id) setActive(null); load(); } };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-start justify-between">
        <div>
          <div className="mono text-xs uppercase tracking-widest text-neutral-500">Module · Processes</div>
          <h1 className="font-display text-4xl font-bold mt-2">Process Library</h1>
          <p className="text-neutral-600 mt-2">Tribal knowledge, made written. Generate SOPs with AI or write your own.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={genOpen} onOpenChange={setGenOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#E54D2E] hover:bg-[#C93E22] text-white rounded-sm btn-lift" data-testid="process-generate-btn"><Sparkles className="h-4 w-4 mr-1" /> Generate with AI</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>AI-Generate an SOP</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Title of the process</Label><Input value={gen.title} onChange={(e)=>setGen({...gen,title:e.target.value})} placeholder="e.g. Onboarding a new customer" className="rounded-sm" data-testid="process-gen-title-input" /></div>
                <div><Label>Additional context (optional)</Label><Textarea rows={4} value={gen.context} onChange={(e)=>setGen({...gen,context:e.target.value})} placeholder="Anything specific about your business, team size, tools used…" className="rounded-sm" data-testid="process-gen-context-input" /></div>
                <Button onClick={generate} disabled={busy} className="w-full bg-neutral-900 hover:bg-neutral-800 rounded-sm" data-testid="process-gen-submit-btn">
                  {busy ? "Generating (Claude Sonnet 5)…" : "Generate SOP"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-sm" data-testid="process-add-btn"><Plus className="h-4 w-4 mr-1" /> Manual</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Process</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Title</Label><Input value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} className="rounded-sm" data-testid="process-title-input" /></div>
                <div><Label>Owner</Label><Input value={form.owner} onChange={(e)=>setForm({...form,owner:e.target.value})} className="rounded-sm" data-testid="process-owner-input" /></div>
                <div><Label>Content (Markdown)</Label><Textarea rows={8} value={form.content} onChange={(e)=>setForm({...form,content:e.target.value})} className="rounded-sm font-mono text-xs" data-testid="process-content-input" /></div>
                <Button onClick={create} className="w-full bg-neutral-900 hover:bg-neutral-800 rounded-sm" data-testid="process-create-submit">Create</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-4 border border-neutral-200 bg-white">
          <div className="mono text-[10px] uppercase text-neutral-500 tracking-widest p-4 border-b border-neutral-200">Library · {processes.length}</div>
          <div className="max-h-[70vh] overflow-y-auto thin-scroll">
            {processes.length === 0 && <div className="p-8 text-center text-neutral-500 text-sm">No SOPs yet.</div>}
            {processes.map((p) => (
              <button key={p.id} onClick={() => setActive(p)} className={`w-full text-left p-4 border-b border-neutral-100 hover:bg-neutral-50 flex items-center gap-3 ${active?.id===p.id?"bg-neutral-100":""}`} data-testid={`process-item-${p.id}`}>
                <FileText className="h-4 w-4 text-[#E54D2E]" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{p.title}</div>
                  <div className="mono text-[10px] text-neutral-500 mt-0.5">{p.owner || "unassigned"}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
        <div className="col-span-12 md:col-span-8">
          {active ? (
            <div className="border border-neutral-200 bg-white p-6" data-testid="process-editor">
              <div className="flex items-center justify-between gap-3">
                <input value={active.title} onChange={(e)=>setActive({...active,title:e.target.value})} className="font-display text-2xl font-bold flex-1 bg-transparent border-0 focus:outline-none focus:ring-0 p-0" data-testid="process-editor-title" />
                <div className="flex gap-2">
                  <Button onClick={save} className="bg-neutral-900 hover:bg-neutral-800 text-white rounded-sm" size="sm" data-testid="process-editor-save">Save</Button>
                  <button onClick={() => remove(active.id)} className="text-neutral-400 hover:text-red-600" data-testid="process-editor-delete"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <Input value={active.owner || ""} onChange={(e)=>setActive({...active,owner:e.target.value})} placeholder="Owner" className="rounded-sm mt-3 max-w-xs" data-testid="process-editor-owner" />
              <Textarea value={active.content} onChange={(e)=>setActive({...active,content:e.target.value})} rows={22} className="rounded-sm mt-4 font-mono text-xs" data-testid="process-editor-content" />
            </div>
          ) : (
            <div className="border border-dashed border-neutral-300 bg-white/50 p-16 text-center text-neutral-500">
              <FileText className="h-8 w-8 mx-auto text-neutral-400" />
              <div className="mt-3">Pick an SOP to view or edit — or generate a new one.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
